/* Struxa calculator figures - ported from the Claude Design v3 file
   (Struxa Gas Strut Calculator v3.dc.html), 03/10/2026.

   Ported rather than reimplemented: every colour, weight, radius and margin
   below is the design's own value. The one deliberate difference is that the
   geometry is fed by Struxa's real solver instead of the design's mock one -
   the design guesses a strut, we know which of the 16 stock lengths it is.

   Fig.02 is NOT WebGL. The design draws its "3D view" as a hand-rolled
   isometric projection on a 2D canvas, which is lighter than the three.js
   scene it replaces and does not lock the renderer up during screenshots.

   State in:  {ang, type, hinge, ns, yaw}
     ang   current opening angle, degrees
     type  'box_lid' | 'top_hinge'            (Box Lid / Window)
     hinge 'Top' | 'Side' | 'Bottom'
     ns    struts per panel, 1 or 2
     yaw   Fig.02 rotation, radians
*/
(function (root) {
  'use strict';

  var ACCENT = '#ff7a00', INK = '#1c1e22';

  // Bracket artwork, traced off Dave's own parts.
  //
  // Side elevations in MILLIMETRES, y negative upward, datum on the face
  // the bracket bolts to - so y=0 is the steel it is screwed against and
  // -y is clear air. Both are a triangle with the ball stud at the top
  // centre; the only difference is which plane the triangle sits in.
  //
  //   flat    the triangle IS the mounting plate, lying on the surface,
  //           stud square to it, so the stud runs along -y.
  //   offset  the triangle stands up off a mounting plate, stud out of
  //           its face, so the stud runs along +x and ends up parallel
  //           to the plate. This is why only the offset can go under a
  //           box rail.
  //
  // ball[1] is each part's own measured standoff. The figure overrides it
  // with the solver's number so the drawing can never contradict the
  // dimension line printed beside it - see brktArt().
  //
  // Holes are even-odd subpaths. Filled with the default winding rule
  // they come out solid and the part reads as a lump.
  var BRKT_ART = {
    flat: {
      d: 'M0 0H31V-3H30L27-7V-11H21V-7L18-3H0Z',
      ball: [24, -15], studAx: 'y'
    },
    offset: {
      d: 'M-20 0H-4.5A4.5 4.5 0 0 0 0-4.5V-14L4-17H8.5V-23H4L0-26V-27H-3' +
         'V-4.5A1.5 1.5 0 0 1-4.5-3H-20Z',
      ball: [12, -20], studAx: 'x'
    }
  };
  // Path2D is in every browser back to 2014, but it is not in node, and
  // the figure tests run there. Cached because drawMounts redraws on
  // every slider move and re-parsing a path string per frame is waste.
  var P2D = {};
  function brktPath(d) {
    if (!(d in P2D)) {
      P2D[d] = (typeof Path2D === 'function') ? new Path2D(d) : null;
    }
    return P2D[d];
  }

  function mono(s) { return '400 ' + s + 'px "IBM Plex Mono", monospace'; }

  // Box height as a fraction of its depth on screen. A constant, so the box
  // is the same shape in Fig.01 and on the fitting sheet; it is the ratio
  // Fig.01 already had, which is the one that was signed off.
  var LID_AR = 0.45;

  // Device-pixel fit, exactly as the design does it.
  function fit(cv) {
    var w = cv.clientWidth, h = cv.clientHeight, d = window.devicePixelRatio || 1;
    cv.width = w * d; cv.height = h * d;
    var ctx = cv.getContext('2d');
    ctx.setTransform(d, 0, 0, d, 0, 0);
    ctx.clearRect(0, 0, w, h);
    return { ctx: ctx, w: w, h: h };
  }

  // Which way the panel swings, and what to call the two bodies. 'Side' is a
  // plan view - that is what the design means by it, and it is why the labels
  // change to SASH/DOOR against a FRAME.
  function geom(S) {
    var isWin = S.type !== 'box_lid';
    if (S.hinge === 'Bottom') return { kind: 'hatch', a0: -Math.PI / 2, s: 1, bDrop: -1, panelLabel: 'HATCH', bodyLabel: 'BODY', view: 'side' };
    if (S.hinge === 'Side') return { kind: 'window', a0: Math.PI / 2, s: -1, bDrop: 1, panelLabel: isWin ? 'SASH' : 'DOOR', bodyLabel: 'FRAME', view: 'plan' };
    if (isWin) return { kind: 'window', a0: Math.PI / 2, s: -1, bDrop: 1, panelLabel: 'SASH', bodyLabel: 'FRAME', view: 'side' };
    return { kind: 'lid', a0: Math.PI, s: 1, bDrop: 1, panelLabel: 'LID', bodyLabel: 'BOX', view: 'side' };
  }

  function draw2d(cv, S) {
    var f = fit(cv), ctx = f.ctx, w = f.w, h = f.h;
    var th = (S.ang || 0) * Math.PI / 180;

    ctx.strokeStyle = '#efefed'; ctx.lineWidth = 1;
    for (var x = 0; x < w; x += 20) { ctx.beginPath(); ctx.moveTo(x + 0.5, 0); ctx.lineTo(x + 0.5, h); ctx.stroke(); }
    for (var y = 0; y < h; y += 20) { ctx.beginPath(); ctx.moveTo(0, y + 0.5); ctx.lineTo(w, y + 0.5); ctx.stroke(); }

    var g = geom(S), pad = 22;
    var boxH = g.kind === 'lid' ? 56 : 92;
    var boxW = Math.min((w - pad * 2) * (g.kind === 'lid' ? 0.9 : 0.5), 300);
    // Keep the lid proportional to the box. The design sizes the box from the
    // canvas width and then clips the lid to whatever headroom is left, which
    // on a short canvas gave a 286px box a 125px lid - it plainly did not
    // cover the thing it is a lid for. Size the box from the headroom instead
    // so the lid always keeps its 86%.
    if (g.kind === 'lid') {
      // The full swing, not the angle on screen. Sizing from the current
      // angle made the box breathe in and out as the lid animated, and gave
      // the fitting sheet's four frames four different box widths.
      // Sized for the full 90 degree swing, not this lid's swing, so the
      // box is the same size whatever the angle sliders say.
      //
      // It used to be sin(maxAng), which made the box 180px wide at 30
      // degrees and 125px at 90. Since every millimetre is scaled by
      // mmK = L / panelLen, resizing the box slides both brackets across
      // the canvas - so dragging the angle looked like it was moving
      // bracket B when the solver had not moved it by a millimetre.
      // Measured in tools/drawn-bracket-drift.test.js, which now fails if
      // the box width changes with either angle slider.
      //
      // The cost is a shallow lid gets the same box as a steep one
      // instead of a bigger one. At the angles people actually use that
      // is 106px against 111px.
      var rise = 1;
      // Height follows width so the box keeps one shape whatever the frame.
      // It was a fixed 56px, so the sheet's taller frame gave it a longer,
      // flatter box than Fig.01 - and the lid, being as long as the box,
      // stretched with it. 58 is the top and bottom margins; solving the box
      // and the open lid in that one budget leaves Fig.01 as it was.
      // The real height-to-depth ratio, so changing box height changes the
      // picture. It used to be a flat LID_AR, which drew a 200mm box and a
      // 900mm box identically. Clamped because a very tall box would leave no
      // room for the lid and a very shallow one would vanish.
      // Capped at 0.62: the box visibly shrinks below ~280mm of height and
      // looks the same above it, which is where box height stops changing the
      // answer anyway (B caps at 70mm below the hinge over 234mm). Uncapped,
      // a 600mm box filled the frame and crushed the lid into a corner.
      var ar = (S.boxH > 0 && S.panelLen > 0) ? (S.boxH / S.panelLen) : LID_AR;
      ar = Math.max(0.25, Math.min(ar, 0.62));
      boxW = Math.min(boxW, (h - 58) / (rise + ar));
      boxW = Math.max(boxW, 120);               // never shrink to nothing
      boxH = Math.round(boxW * ar);
    }
    var x0 = g.kind === 'lid' ? pad + (w - pad * 2 - boxW) / 2 : pad + 10;
    var y0 = g.kind === 'window' ? 26 : h - 34 - boxH;
    var hx = x0 + boxW, hy = g.kind === 'hatch' ? y0 + boxH : y0;

    var dirAt = function (t) { var a = g.a0 + g.s * t; return [Math.cos(a), Math.sin(a)]; };
    var edge = 24;
    var cap = function (d) {
      var mx = d[0] < -0.01 ? (hx - pad) / -d[0] : d[0] > 0.01 ? (w - pad - hx) / d[0] : 1e5;
      var my = d[1] < -0.01 ? (hy - edge) / -d[1] : d[1] > 0.01 ? (h - edge - hy) / d[1] : 1e5;
      return Math.min(mx, my);
    };
    // A lid is as long as the box it covers. The design drew it at 86%, so
    // closed it stopped short of the far edge and never looked shut - the 3D
    // uses the full depth, which is why it closed cleanly there and not here.
    var tiltR = (-(S.tilt || 0)) * Math.PI / 180;
    // Cap the panel against the frame in the direction it points AFTER the
    // tilt, otherwise a sloped box throws the lid off the canvas.
    var capAt = function (t) {
      var a = g.a0 + g.s * t + tiltR; return [Math.cos(a), Math.sin(a)];
    };
    // Two things have to hold at once. The panel cannot change length as it
    // swings, because the brackets are scaled off it (mmK = L / panelLen) and
    // they are bolted on. And a lid is exactly as long as the box it covers.
    //
    // Capping the PANEL against the frame satisfied the first and broke the
    // second: a 106px lid on a 120px box, short of its own front edge at
    // every angle. So cap across the whole sweep, then shrink the BOX until
    // the panel fits at full length, rather than trimming the panel.
    var maxSw = ((S.maxAng == null ? S.ang : S.maxAng) || 0) * Math.PI / 180;
    var sweepCap = function () {
      var c = cap(capAt(0));
      for (var q = 1; q <= 12; q++) { c = Math.min(c, cap(capAt(maxSw * q / 12))); }
      return c;
    };
    var Lcap = sweepCap();
    if (g.kind === 'lid') {
      for (var it = 0; it < 24 && Lcap < boxW - 0.5; it++) {
        boxW = Math.max(60, Lcap);
        boxH = Math.round(boxW * ar);
        x0 = pad + (w - pad * 2 - boxW) / 2;
        y0 = h - 34 - boxH;
        hx = x0 + boxW; hy = y0;
        Lcap = sweepCap();
      }
    }
    var L = Math.min(g.kind === 'lid' ? boxW : boxH * 1.1, Lcap);
    // Millimetres to pixels, off the panel. Needed before the box is drawn
    // so the frame rail can be drawn inside it at the right depth.
    var mmK = (S.panelLen > 0) ? L / S.panelLen : 0;

    // The box, its lid and both brackets are one assembly sitting on a
    // slope, so the slope goes on the transform and everything drawn after
    // this inherits it. Rotating the box on its own left the lid level and
    // visibly off the box. The grid stays put - it is the level reference.
    ctx.save();
    if (tiltR) {
      var ct0 = Math.cos(tiltR), st0 = Math.sin(tiltR);
      var rotP = function (px, py) {
        var dx = px - hx, dy = py - hy;
        return [hx + dx * ct0 - dy * st0, hy + dx * st0 + dy * ct0];
      };
      var cs = [[x0, y0], [x0 + boxW, y0], [x0 + boxW, y0 + boxH], [x0, y0 + boxH]]
        .map(function (p) { return rotP(p[0], p[1]); });
      var ys = cs.map(function (p) { return p[1]; });
      var xs = cs.map(function (p) { return p[0]; });
      // nudge back inside the frame if a corner swung out of it
      var dy = Math.max(0, 6 - Math.min.apply(null, ys))
             - Math.max(0, Math.max.apply(null, ys) - (h - 6));
      var dx = Math.max(0, 6 - Math.min.apply(null, xs))
             - Math.max(0, Math.max.apply(null, xs) - (w - 6));
      ctx.translate(dx, dy);
      ctx.translate(hx, hy); ctx.rotate(tiltR); ctx.translate(-hx, -hy);
    }
    ctx.fillStyle = '#f4f4f2'; ctx.strokeStyle = INK; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.rect(x0, y0, boxW, boxH); ctx.fill(); ctx.stroke();

    // The frame rail, banding the inside top of the box. Drawn because it is
    // the whole reason bracket B moves down: B has to land on solid side
    // BELOW this, not on it, and not packed out past it.
    var railPx = (g.kind === 'lid' && mmK && S.railMM > 0)
      ? Math.min(S.railMM * mmK, boxH - 6) : 0;
    if (railPx > 2) {
      ctx.fillStyle = 'rgba(255,122,0,.18)';
      ctx.fillRect(x0 + 1, y0 + 1, boxW - 2, railPx);
      ctx.strokeStyle = ACCENT; ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(x0 + 1, y0 + railPx); ctx.lineTo(x0 + boxW - 1, y0 + railPx);
      ctx.stroke();
      ctx.fillStyle = '#b4631b'; ctx.font = mono(9);
      ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
      if (railPx > 11) { ctx.fillText('RAIL', x0 + 7, y0 + railPx - 4); }
    }

    var d0 = dirAt(0);
    ctx.strokeStyle = '#a8a8a4'; ctx.lineWidth = 2; ctx.setLineDash([6, 5]);
    ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(hx + d0[0] * L, hy + d0[1] * L); ctx.stroke(); ctx.setLineDash([]);

    // The sweep, split where the strut takes over. Below that angle the
    // person is lifting; above it the strut carries the panel. It does not
    // close itself from up there - closing is a push down - so the arrow
    // below says which way you have to lean on it, not which way it falls.
    var arcR = 40;
    var maxA2 = (S.maxAng == null ? S.ang : S.maxAng) || 0;
    var kickD = S.kick == null ? null : Math.max(0, Math.min(S.kick, maxA2));
    var maxT = maxA2 * Math.PI / 180;
    ctx.lineWidth = 5; ctx.lineCap = 'butt';
    if (maxT > 0 && kickD != null) {
      var kickT = kickD * Math.PI / 180;
      ctx.strokeStyle = '#e4e4e0';                 // you are lifting
      ctx.beginPath(); ctx.arc(hx, hy, arcR, g.a0, g.a0 + g.s * kickT, g.s < 0); ctx.stroke();
      ctx.strokeStyle = 'rgba(255,122,0,.42)';     // the strut has it
      ctx.beginPath(); ctx.arc(hx, hy, arcR, g.a0 + g.s * kickT, g.a0 + g.s * maxT, g.s < 0); ctx.stroke();
      var kx = Math.cos(g.a0 + g.s * kickT), ky = Math.sin(g.a0 + g.s * kickT);
      ctx.strokeStyle = '#ff7a00'; ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(hx + kx * (arcR - 7), hy + ky * (arcR - 7));
      ctx.lineTo(hx + kx * (arcR + 7), hy + ky * (arcR + 7));
      ctx.stroke();
    }
    ctx.lineWidth = 1.5; ctx.strokeStyle = '#d8d8d4';
    ctx.beginPath(); ctx.arc(hx, hy, arcR, g.a0, g.a0 + g.s * th, g.s < 0); ctx.stroke();

    var dp = dirAt(th);
    var lx = hx + dp[0] * L, ly = hy + dp[1] * L;
    ctx.strokeStyle = INK; ctx.lineWidth = 4; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(lx, ly); ctx.stroke();

    // Bracket positions come from the solver, in millimetres, scaled by the
    // panel. They used to be fractions of the drawn box - 44% forward and 34%
    // down - which put B 198mm and 204mm out where the solver says 27mm and
    // 70mm. Fig.03 already drew the solved positions, so the two figures
    // contradicted each other on the same page. The fractions remain as a
    // fallback for callers that do not pass the millimetres.
    var fwd = (mmK && S.bMM != null) ? S.bMM * mmK
            : boxW * (g.kind === 'lid' ? 0.44 : 0.52);
    var drop = (mmK && S.cMM != null) ? S.cMM * mmK
             : boxH * (g.kind === 'lid' ? 0.34 : 0.24);
    var aLen = (mmK && S.aMM != null) ? S.aMM * mmK : L * 0.65;
    var bx = hx - fwd, by = hy + g.bDrop * drop;
    // The panel has a frame of its own and bracket A bolts to its INNER
    // FACE, which is where the solver's standoff comes from - so the panel
    // gets its rail drawn and A gets lifted off the skin onto it. Which side
    // is "inner" is settled by asking which normal points towards bracket B;
    // that holds for a lid, a sash or a hatch without a per-type table.
    var nA = [-dp[1], dp[0]];
    if ((bx - hx) * nA[0] + (by - hy) * nA[1] < 0) { nA = [dp[1], -dp[0]]; }
    var railA = (mmK && S.lidRailMM > 0) ? S.lidRailMM * mmK : 0;
    var ballA = (mmK && S.ballDropMM > 0) ? S.ballDropMM * mmK : 0;
    var tx = hx + dp[0] * aLen + nA[0] * ballA,
        ty = hy + dp[1] * aLen + nA[1] * ballA;

    // The same black bar as the lid end. Both ends are mounting points;
    // drawing one of them as a detailed part invites someone to match it.
    // The bar is centred on the solved point. It used to sit at
    // bx + plW - plT/2, left over from the modelled plate where bx was the
    // plate's left edge and the ball sat at its far end - which drew bracket B
    // 21.5px right of its real position, outside the box once the solver's
    // true 27mm replaced the old hardcoded 198mm.
    if (railA > 1.5) {
      ctx.fillStyle = 'rgba(255,122,0,.26)';
      ctx.beginPath();
      ctx.moveTo(hx, hy); ctx.lineTo(lx, ly);
      ctx.lineTo(lx + nA[0] * railA, ly + nA[1] * railA);
      ctx.lineTo(hx + nA[0] * railA, hy + nA[1] * railA);
      ctx.closePath(); ctx.fill();
      ctx.strokeStyle = ACCENT; ctx.lineWidth = 1.2; ctx.stroke();
    }
    ctx.fillStyle = INK;
    ctx.fillRect(bx - 12, by - 3, 24, 6);

    ctx.save(); ctx.translate(tx, ty); ctx.rotate(g.a0 + g.s * th);
    ctx.fillStyle = INK; ctx.fillRect(-12, -3, 24, 6);
    ctx.restore();

    var pinX = bx, pinY = by;
    var ang = Math.atan2(ty - pinY, tx - pinX), len = Math.hypot(tx - pinX, ty - pinY);
    ctx.save(); ctx.translate(pinX, pinY); ctx.rotate(ang);
    ctx.strokeStyle = '#55555a'; ctx.lineWidth = 4; ctx.lineCap = 'butt';
    ctx.beginPath(); ctx.moveTo(4, 0); ctx.lineTo(len * 0.46, 0); ctx.stroke();
    ctx.strokeStyle = ACCENT; ctx.lineWidth = 8;
    ctx.beginPath(); ctx.moveTo(len * 0.44, 0); ctx.lineTo(len - 4, 0); ctx.stroke();
    ctx.restore();

    ctx.lineWidth = 2; ctx.strokeStyle = INK;
    [[pinX, pinY], [tx, ty]].forEach(function (p) {
      ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(p[0], p[1], 4.5, 0, 7); ctx.fill(); ctx.stroke();
    });
    ctx.fillStyle = '#fff'; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.arc(hx, hy, 6, 0, 7); ctx.fill(); ctx.stroke();
    ctx.fillStyle = INK; ctx.beginPath(); ctx.arc(hx, hy, 2, 0, 7); ctx.fill();

    // Which way the installer has to lean on it at this point in the travel.
    // Under the hand-over angle the strut is not carrying it yet, so opening
    // is a pull up. Once the strut has it, the panel stays put and closing
    // it is a push down. Animating without this shows motion but says
    // nothing about the effort, which is the thing being bought.
    if (kickD != null && maxA2 > 0) {
      var lifting = (S.ang || 0) < kickD;
      // Pushed out along the panel and offset perpendicular, because on the
      // centreline the label landed on top of the strut.
      var pX = dp[1], pY = -dp[0];
      var ax = lx - dp[0] * L * 0.16 + pX * 15,
          ay = ly - dp[1] * L * 0.16 + pY * 15;
      var dir = lifting ? -1 : 1;              // up while lifting, down to shut
      // Named apart from aLen above, which is bracket A's distance along
      // the panel - the same var was being declared twice in one function.
      var arrowLen = 22;
      ctx.strokeStyle = '#ff7a00'; ctx.fillStyle = '#ff7a00';
      ctx.lineWidth = 2.4; ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(ax, ay - dir * arrowLen * 0.5);
      ctx.lineTo(ax, ay + dir * arrowLen * 0.5);
      ctx.stroke();
      var tipY = ay + dir * arrowLen * 0.5;
      ctx.beginPath();
      ctx.moveTo(ax, tipY + dir * 5);
      ctx.lineTo(ax - 5, tipY - dir * 3);
      ctx.lineTo(ax + 5, tipY - dir * 3);
      ctx.closePath(); ctx.fill();
      ctx.font = '500 9px "IBM Plex Mono", monospace';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(lifting ? 'LIFT' : 'PUSH TO SHUT', ax, tipY + dir * 15);
    }
    ctx.fillStyle = '#8a8a86'; ctx.font = mono(9); ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';
    if (g.kind === 'lid') {
      var tw = ctx.measureText('HINGE').width;
      ctx.fillText('HINGE', Math.min(hx + 10, w - tw - 4), hy - 10);
    } else {
      ctx.fillText('HINGE', hx + 10, hy + (g.kind === 'window' ? -9 : 17));
    }
    ctx.textAlign = dp[0] < -0.2 ? 'right' : dp[0] > 0.2 ? 'left' : 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(g.panelLabel, lx + dp[0] * 10, ly + dp[1] * 10);
    ctx.textBaseline = 'alphabetic'; ctx.textAlign = 'left';
    ctx.fillText(g.bodyLabel, x0 + 8, y0 + boxH - 9);
    // labelR, not ar - ar is the box aspect ratio declared above.
    var am = g.a0 + g.s * th * 0.22, labelR = arcR - 14;
    ctx.fillStyle = INK; ctx.font = '500 11px "IBM Plex Mono", monospace'; ctx.textAlign = 'center';
    ctx.fillText((S.ang || 0) + '°', hx + Math.cos(am) * labelR, hy + Math.sin(am) * labelR + 4);
    ctx.font = '500 10px "IBM Plex Mono", monospace';
    if (g.kind === 'lid') { ctx.textAlign = 'right'; ctx.fillText('A', tx - 10, ty - 8); }
    else { ctx.textAlign = 'left'; ctx.fillText('A', tx + 10, ty + 16); }
    ctx.textAlign = 'center';
    ctx.fillText('B', bx, by + 18);
    ctx.textAlign = 'left';
    ctx.restore();
  }

  // ── Fig.03, where to drill ────────────────────────────────────────────────
  // This replaced three hand-drawn static SVGs (box lid, window, and a hatch
  // for a type Struxa does not sell). They never moved: the box-lid one drew a
  // 480-wide box with a 357-long lid, so the lid was 26% short of the box it
  // covers, bracket B was a pair of rectangles in an L, and none of it matched
  // the A/B/C millimetres printed beside it. A drawing that contradicts the
  // numbers next to it is worse than no drawing.
  //
  // Everything here comes from the solver: a, b and c are the same values the
  // table shows, so the picture and the numbers cannot disagree.
  function drawDrill(cv, S) {
    var f = fit(cv), ctx = f.ctx, w = f.w, h = f.h;
    var panel = Math.max(50, S.panelLen || 450);     // mm along the swing
    var boxH = Math.max(40, S.boxH || 300);          // mm of wall below the hinge
    // The real open angle. This was capped at 24 degrees because at the full
    // 68 a 450mm lid needs 417mm of headroom and the drawing collapsed; the
    // canvas is taller now instead, so the panel can be shown where it
    // actually ends up.
    var th = (S.maxAng || 68) * Math.PI / 180;
    var aM = Math.max(0, S.a || 0), bM = Math.max(0, S.b || 0), cM = Math.max(0, S.c || 0);

    ctx.strokeStyle = '#efefed'; ctx.lineWidth = 1;
    for (var gx = 0; gx < w; gx += 20) { ctx.beginPath(); ctx.moveTo(gx + .5, 0); ctx.lineTo(gx + .5, h); ctx.stroke(); }
    for (var gy = 0; gy < h; gy += 20) { ctx.beginPath(); ctx.moveTo(0, gy + .5); ctx.lineTo(w, gy + .5); ctx.stroke(); }

    // Fit the whole assembly - box, and the lid at full open - into the frame.
    // Only the top of the wall is worth drawing - enough to carry bracket B
    // and read as a box. The full 600mm would crush everything else flat.
    // Still capped: 600mm of box below the hinge would flatten the lid again,
    // and only the top of the wall carries bracket B. Tighter than before to
    // buy headroom for the full swing.
    var wall = Math.min(boxH, Math.max(cM * 2.5, panel * 0.34));
    var mL = 54, mR = 58, mT = 24, mB = 50;
    var needW = panel, needH = panel * Math.max(Math.sin(th), 0.25) + wall;
    var k = Math.min((w - mL - mR) / needW, (h - mT - mB) / needH);
    if (!isFinite(k) || k <= 0) return;

    var hx = mL + panel * k, hy = mT + panel * Math.max(Math.sin(th), 0.25) * k;
    var wallPx = wall * k;
    var tiltR = (-(S.tilt || 0)) * Math.PI / 180;
    ctx.save();
    if (tiltR) { ctx.translate(hx, hy); ctx.rotate(tiltR); ctx.translate(-hx, -hy); }

    // the box
    ctx.fillStyle = '#f4f4f2'; ctx.strokeStyle = INK; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.rect(hx - panel * k, hy, panel * k, wallPx); ctx.fill(); ctx.stroke();

    // The frame rail. This is the sheet someone drills off, so the thing
    // bracket B must clear belongs on it.
    var railM = Math.max(0, S.rail || 0);
    var railPx = railM > 0 ? Math.min(railM * k, wallPx - 4) : 0;
    if (railPx > 2) {
      ctx.fillStyle = 'rgba(255,122,0,.18)';
      ctx.fillRect(hx - panel * k + 1, hy + 1, panel * k - 2, railPx);
      ctx.strokeStyle = ACCENT; ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(hx - panel * k + 1, hy + railPx);
      ctx.lineTo(hx - 1, hy + railPx);
      ctx.stroke();
      ctx.fillStyle = '#b4631b'; ctx.font = mono(9);
      ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
      if (railPx > 11) { ctx.fillText('FRAME RAIL', hx - panel * k + 7, hy + railPx - 4); }
    }

    // the lid, shut (dashed) and open (solid). Both are exactly panel long,
    // because a lid is as long as the box it covers.
    ctx.strokeStyle = '#a8a8a4'; ctx.lineWidth = 2; ctx.setLineDash([7, 6]);
    ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(hx - panel * k, hy); ctx.stroke();
    ctx.setLineDash([]);
    var lx = hx - Math.cos(th) * panel * k, ly = hy - Math.sin(th) * panel * k;
    ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(lx, ly); ctx.stroke();

    // A on the panel, B on the wall - the solver's own coordinates. A is
    // lifted off the skin onto the inner face of the lid's own frame rail,
    // which is drawn, because that standoff is what the solver uses.
    var lidRailPx = Math.max(0, (S.lidRail || 0) * k);
    var ballPx = Math.max(0, (S.ballDrop || 0) * k);
    var nLx = -Math.sin(th), nLy = Math.cos(th);     // towards the box
    var ax = hx - Math.cos(th) * aM * k + nLx * ballPx;
    var ay = hy - Math.sin(th) * aM * k + nLy * ballPx;
    var bx = hx - bM * k, by = hy + cM * k;
    if (lidRailPx > 1.5) {
      var lxe = hx - Math.cos(th) * panel * k, lye = hy - Math.sin(th) * panel * k;
      ctx.fillStyle = 'rgba(255,122,0,.26)';
      ctx.beginPath();
      ctx.moveTo(hx, hy); ctx.lineTo(lxe, lye);
      ctx.lineTo(lxe + nLx * lidRailPx, lye + nLy * lidRailPx);
      ctx.lineTo(hx + nLx * lidRailPx, hy + nLy * lidRailPx);
      ctx.closePath(); ctx.fill();
      ctx.strokeStyle = ACCENT; ctx.lineWidth = 1.2; ctx.stroke();
    }

    // the strut, rod end at B
    var ang = Math.atan2(ay - by, ax - bx), len = Math.hypot(ax - bx, ay - by);
    ctx.save(); ctx.translate(bx, by); ctx.rotate(ang);
    ctx.strokeStyle = '#55555a'; ctx.lineWidth = 4; ctx.lineCap = 'butt';
    ctx.beginPath(); ctx.moveTo(4, 0); ctx.lineTo(len * 0.46, 0); ctx.stroke();
    ctx.strokeStyle = ACCENT; ctx.lineWidth = 8;
    ctx.beginPath(); ctx.moveTo(len * 0.44, 0); ctx.lineTo(len - 4, 0); ctx.stroke();
    ctx.restore();

    // Both brackets are the same black bar used in Fig.01. Bracket A lies
    // ALONG the lid: the lid runs in direction (-cos th, -sin th), whose
    // angle is pi + th, and a symmetric bar at th draws the same. It was
    // -th, the mirror, which tilted the bar 48 degrees off the lid.
    ctx.fillStyle = INK;
    ctx.save(); ctx.translate(ax, ay); ctx.rotate(th);
    ctx.fillRect(-12, -3, 24, 6); ctx.restore();
    ctx.fillRect(bx - 12, by - 3, 24, 6);

    // pivots
    ctx.lineWidth = 2; ctx.strokeStyle = INK;
    [[ax, ay], [bx, by]].forEach(function (p) {
      ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(p[0], p[1], 4.5, 0, 7); ctx.fill(); ctx.stroke();
    });
    ctx.fillStyle = '#fff'; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.arc(hx, hy, 6, 0, 7); ctx.fill(); ctx.stroke();
    ctx.fillStyle = INK; ctx.beginPath(); ctx.arc(hx, hy, 2, 0, 7); ctx.fill();

    // ── dimensions ──────────────────────────────────────────────────
    // These are drafting lines, not parts. Drawn in the strut's own orange at
    // the same weight, the A dimension ran straight through the strut and
    // could not be told from it, and the C label sat centred on its own line,
    // with the line through the letter. Thin grey lines, dashed witness lines
    // back to what they measure, and the label in a white box pushed off the
    // line along the given normal.
    function boxed(label, mx, my) {
      ctx.font = '700 12px "IBM Plex Mono", monospace';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      var tw = ctx.measureText(label).width;
      ctx.fillStyle = '#fff';
      ctx.fillRect(mx - tw / 2 - 5, my - 9, tw + 10, 18);
      ctx.strokeStyle = ACCENT; ctx.lineWidth = 1.2;
      ctx.strokeRect(mx - tw / 2 - 5, my - 9, tw + 10, 18);
      ctx.fillStyle = ACCENT; ctx.fillText(label, mx, my);
    }
    function dim(x1, y1, x2, y2, label, dnx, dny, fx1, fy1, fx2, fy2) {
      ctx.strokeStyle = '#a8a8a4'; ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(fx1, fy1); ctx.lineTo(x1, y1);
      ctx.moveTo(fx2, fy2); ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.strokeStyle = '#55555a'; ctx.fillStyle = '#55555a'; ctx.lineWidth = 1.3;
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      var a2 = Math.atan2(y2 - y1, x2 - x1);
      [[x1, y1, a2 + Math.PI], [x2, y2, a2]].forEach(function (e) {
        ctx.save(); ctx.translate(e[0], e[1]); ctx.rotate(e[2]);
        ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(-7, -2.8); ctx.lineTo(-7, 2.8);
        ctx.closePath(); ctx.fill(); ctx.restore();
      });
      boxed(label, (x1 + x2) / 2 + dnx * 13, (y1 + y2) / 2 + dny * 13);
    }
    // A runs parallel to the lid, offset on the side away from the strut.
    var dnx = -Math.sin(th), dny = -Math.cos(th), off = 30;
    dim(hx + dnx * off, hy + dny * off, ax + dnx * off, ay + dny * off, 'A',
        dnx, dny, hx, hy, ax, ay);
    dim(hx, hy + wallPx + 24, bx, hy + wallPx + 24, 'B', 0, 1, hx, hy, bx, by);
    dim(bx - 28, hy, bx - 28, by, 'C', -1, 0, hx, hy, bx, by);

    // labels
    ctx.fillStyle = '#8a8a86'; ctx.font = mono(9);
    ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
    ctx.fillText('BOX', hx - panel * k + 8, hy + wallPx - 9);
    ctx.textAlign = 'right';
    ctx.fillText('LID OPEN', lx - 8, ly - 4);
    ctx.textAlign = 'left';
    ctx.fillText('HINGE', hx + 10, hy + 4);
    // Bracket names sit beyond their own pivot - along the lid for A, below
    // the wall for B - each on a white patch. Pushing A out perpendicular put
    // it exactly where the A dimension now lives.
    function tag(label, x, y) {
      ctx.font = '500 10px "IBM Plex Mono", monospace';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      var tw = ctx.measureText(label).width;
      ctx.fillStyle = '#fff'; ctx.fillRect(x - tw / 2 - 3, y - 7, tw + 6, 14);
      ctx.fillStyle = INK; ctx.fillText(label, x, y);
    }
    tag('BRACKET A', ax - Math.cos(th) * 44, ay - Math.sin(th) * 44);
    tag('BRACKET B', bx, by + 24);
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';
    ctx.restore();
  }

  // ── Fig.04, how the brackets bolt on ────────────────────────────────────
  // Two sections, side by side. Left: the top of the box side. Right: the
  // edge of the lid. Both drawn from the rail depths actually entered, so
  // the no-rail case shows the bracket on the wall and the framed case shows
  // it under the rail - which is the whole difference being explained.
  // ── Fig.04, where the brackets go and what goes on them ────────────────
  // The whole box in section once, then the two places you drill magnified.
  // Two details with no surroundings could not say WHERE in the box they
  // were, which is the thing someone who has not done this before needs.
  function drawMounts(cv, S) {
    var f = fit(cv), ctx = f.ctx, w = f.w, h = f.h;
    var panel = Math.max(120, S.panelLen || 450);
    var boxH = Math.max(80, S.boxH || 400);
    var boxRail = Math.max(0, S.railMM || 0);
    var lidRail = Math.max(0, S.lidRailMM || 0);
    // The skirt of a lid that wraps the box. Outside the walls, so it never
    // meets the strut - drawn because leaving it off makes a lipped lid look
    // like a bare panel, but it carries no bracket.
    var lidSkirt = Math.max(0, S.lidSkirtMM || 0);
    // A lid cannot both wrap the OUTSIDE of the walls and carry a frame
    // INSIDE the opening - they are the two halves of one choice. sx-v3.js
    // splits them at st() so only one is ever non-zero, but nothing made
    // the figure itself hold to it, and handed both it would draw a lid
    // that does both at once and put the two dimensions on top of each
    // other in the left margin. The skirt wins: it is the outer shape, and
    // it is what a wrapping lid actually has.
    if (lidSkirt > 0) { lidRail = 0; }
    var aM = Math.max(0, S.aMM || 0), bM = Math.max(0, S.bMM || 0);
    var cM = Math.max(0, S.cMM || 70), ballD = Math.max(0, S.ballDropMM || 0);
    var brkA = S.brkA === 'offset' ? 'offset' : 'flat';
    var brkB = S.brkB === 'offset' ? 'offset' : 'flat';
    // A sash, not a box lid. The solver has no rail term for a window, and
    // those two sliders are hidden on one, so none of the rail drawing
    // below applies and the words have to change with it.
    var isWin = S.type === 'top_hinge';
    if (isWin) { boxRail = 0; lidRail = 0; }

    ctx.clearRect(0, 0, w, h);
    // 0.36, not 0.46. The lower half used to be two small swatches; it is
    // now the full section with both brackets and every standoff on it,
    // which is the drawing someone actually works from.
    // The overview is gone - figures 01 to 03 already show the box with
    // its lid and strut, and repeating it here cost two thirds of the
    // canvas. The section now has all of it.
    var botY = 0;

    // Lived in the overview block that went with it; the section still
    // leans on it for every label.
    function tag(txt, x, y, align, col) {
      ctx.fillStyle = col || '#8a8a86'; ctx.font = mono(9);
      ctx.textAlign = align || 'left'; ctx.textBaseline = 'middle';
      ctx.fillText(txt, x, y);
    }

    // ── the section ────────────────────────────────────────────────────
    var half = w / 2, dH = h - 30, dTop = 20;

    // The two details below are the box-lid mounting arrangement - bracket
    // under a side rail, bracket on the lid rail. A sash mounts differently
    // and the window solve has no rail term at all, so drawing them here
    // would be showing a customer an arrangement that is not theirs.
    //
    // Not replaced with a guessed window detail: the bracket positions it
    // would have to show are the ones that decide the strut length, and
    // inventing those is how a pressure comes out wrong. The A/B/C table
    // beside this figure still gives a window its real numbers from the
    // solver.
    if (isWin) {
      ctx.fillStyle = '#8a8a86';
      ctx.font = mono(10);
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('Mounting detail for a sash is not drawn yet.',
                   w / 2, h / 2 - 8);
      ctx.fillText('Figures 01 to 03 and the A/B/C table are from the solver.',
                   w / 2, h / 2 + 8);
      return;
    }
    // Bigger: the first pass left these at about two thirds of the width
    // they had, with the brackets small inside them.
    var DS = Math.max(0.7, Math.min(2.1, (dH - 16) / 100));
    var dmm = function (v) { return Math.max(0, v * DS); };

    // Three, because both real brackets have three. Two reads as a
    // generic tab; three is the thing in the photograph.
    function holes(x, y, ww, hh, vertical) {
      ctx.fillStyle = '#fff';
      [0.2, 0.5, 0.8].map(function (f) {
        return vertical ? [x + ww / 2, y + hh * f] : [x + ww * f, y + hh / 2];
      }).forEach(function (p) {
        ctx.beginPath(); ctx.arc(p[0], p[1], 1.4, 0, 7); ctx.fill();
      });
    }
    // The turned boss the ball stud rises out of. Small, but it is the
    // thing that makes a drawn rectangle read as a bracket.
    function boss(x, y, dir, vertical) {
      ctx.fillStyle = '#2b2b31';
      if (vertical) { ctx.fillRect(x - 3, y + (dir > 0 ? 0 : -5), 6, 5); }
      else { ctx.fillRect(x + (dir > 0 ? 0 : -5), y - 3, 5, 6); }
    }
    // An L-bracket seen on edge: long plate, 90 degree bend, short web with
    // the stud out of it. plateDir is which way the plate runs from the
    // corner, webDir which way the web drops.
    function lBracket(cx, cy, plateLen, webLen, plateDir, webDir) {
      var th = 6;
      ctx.fillStyle = INK;
      ctx.fillRect(Math.min(cx, cx + plateDir * plateLen), cy,
                   plateLen, th);                                  // the plate
      ctx.fillRect(cx + (plateDir > 0 ? 0 : -th), cy + (webDir > 0 ? th : -webLen),
                   th, webLen);                                     // the web
      holes(Math.min(cx, cx + plateDir * plateLen), cy, plateLen, th, false);
      // the fold, so the corner reads as bent rather than welded
      ctx.strokeStyle = '#6e6e6b'; ctx.lineWidth = 0.9;
      ctx.beginPath();
      ctx.moveTo(cx + (plateDir > 0 ? th : -th), cy + 1);
      ctx.lineTo(cx + (plateDir > 0 ? th : -th), cy + th - 1);
      ctx.stroke();
    }
    // The ball stud, hard against the bracket, and nothing else.
    //
    // This used to draw the socket clipped over the ball and 30px of
    // strut running off, labelled "strut" and "end fitting". Those are a
    // different subject: these panels answer where the bracket goes and
    // which way the ball faces, and the strut arriving later crowded the
    // thing being explained. It also held the ball well off the plate, so
    // a short stud read as a long spigot.
    function ballStud(x, y, dir) {
      ctx.strokeStyle = '#2b2b31'; ctx.lineWidth = 3.4; ctx.lineCap = 'butt';
      ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + dir * 4, y); ctx.stroke();
      ctx.fillStyle = ACCENT; ctx.strokeStyle = INK; ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.arc(x + dir * 8, y, 4.6, 0, 7); ctx.fill(); ctx.stroke();
    }
    // Both captions are centred in their own half, and neither was measured
    // against it - so on a narrow canvas the left caption's tail ran into
    // the right caption's head and the two sentences read as one. Shrink to
    // fit, and only ellipsize if shrinking is not enough.
    function capFit(txt, cx, y, maxW, weight, size, col) {
      var s = size;
      ctx.fillStyle = col;
      for (; s >= 7; s--) {
        ctx.font = weight + ' ' + s + 'px "IBM Plex Mono", monospace';
        if (ctx.measureText(txt).width <= maxW) { break; }
      }
      var out = txt;
      while (out.length > 4 && ctx.measureText(out + '…').width > maxW) {
        out = out.slice(0, -1);
      }
      if (out !== txt) { out += '…'; }
      ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
      ctx.fillText(out, cx, y);
    }
    function cap2(l1, l2, cx) {
      var maxW = half - 18;          // its own half, less a gutter either side
      capFit(l1, cx, h - 16, maxW, '600', 10, '#1c1e22');
      capFit(l2, cx, h - 4, maxW, '400', 9, '#8a8a86');
    }
    function numTag(num, x, y) {
      ctx.fillStyle = INK; ctx.beginPath(); ctx.arc(x, y, 8, 0, 7); ctx.fill();
      ctx.fillStyle = '#fff'; ctx.font = '700 10px "IBM Plex Mono", monospace';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(String(num), x, y);
    }

    // ── shared furniture for both details ──────────────────────────────
    //
    // The two panels used to be the same picture with different captions.
    // Each now carries the one thing that says which surface you are on:
    // detail 1 has the box's top edge above its rail, detail 2 has the
    // lid's skin above its rail, and both carry an up arrow, because a
    // grey rectangle on its own has no top.
    function wallFace(x, y, ww, hh) {
      ctx.fillStyle = '#f2f2ef'; ctx.fillRect(x, y, ww, hh);
      ctx.strokeStyle = '#c9c9c4'; ctx.lineWidth = 1.1;
      ctx.strokeRect(x + 0.5, y + 0.5, ww - 1, hh - 1);
    }
    // The skin the rail hangs off, drawn solid so it reads as the edge of
    // the panel rather than another surface you could drill.
    // No text on the bar. It used to carry "top of the box" / "lid skin"
    // 13px above the panel, which is 3px under the sub-panel's own label.
    // The sub-label and the UP arrow already say which way is up.
    function skin(x, y, ww) {
      ctx.fillStyle = '#d8d8d4'; ctx.fillRect(x, y - 7, ww, 7);
      ctx.strokeStyle = INK; ctx.lineWidth = 1.4;
      ctx.strokeRect(x + 0.5, y - 6.5, ww - 1, 6);
    }
    function upArrow(x, y) {
      ctx.strokeStyle = '#a8a8a4'; ctx.lineWidth = 1.1;
      ctx.beginPath();
      ctx.moveTo(x, y + 13); ctx.lineTo(x, y);
      ctx.moveTo(x - 3.5, y + 4); ctx.lineTo(x, y); ctx.lineTo(x + 3.5, y + 4);
      ctx.stroke();
      tag('UP', x + 6, y + 6, 'left', '#a8a8a4');
    }
    // A plate you are looking square at: you see its face and its holes.
    function plateFront(cx, cy, pw, ph) {
      ctx.fillStyle = INK; ctx.fillRect(cx - pw / 2, cy - ph / 2, pw, ph);
      holes(cx - pw / 2, cy - ph / 2, pw, ph, false);
    }
    // A plate you are looking along the edge of - bolted to a horizontal
    // face, so from the side it is a bar and its holes are out of sight.
    // Drawing the holes here is what made detail 1 contradict its caption.
    function plateEdge(cx, cy, pw) {
      ctx.fillStyle = INK; ctx.fillRect(cx - pw / 2, cy - 3, pw, 6);
      ctx.strokeStyle = '#6e6e6b'; ctx.lineWidth = 0.9;
      ctx.beginPath(); ctx.moveTo(cx - pw / 2, cy); ctx.lineTo(cx + pw / 2, cy);
      ctx.stroke();
    }
    function ballFront(cx, cy) {
      ctx.fillStyle = '#2b2b31';
      ctx.beginPath(); ctx.arc(cx, cy, 8, 0, 7); ctx.fill();
      ctx.fillStyle = ACCENT; ctx.strokeStyle = INK; ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.arc(cx, cy, 5.6, 0, 7); ctx.fill(); ctx.stroke();
    }
    // A dimension from the panel edge down to the ball, so the number in
    // the C row of the fitting sheet can be found in the picture.
    function dropDim(x, yTop, yBot, txt) {
      if (yBot - yTop < 14) { return; }
      ctx.strokeStyle = '#8a8a86'; ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath(); ctx.moveTo(x, yTop); ctx.lineTo(x, yBot); ctx.stroke();
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(x - 3, yTop + 4); ctx.lineTo(x, yTop); ctx.lineTo(x + 3, yTop + 4);
      ctx.moveTo(x - 3, yBot - 4); ctx.lineTo(x, yBot); ctx.lineTo(x + 3, yBot - 4);
      ctx.stroke();
      tag(txt, x + 5, (yTop + yBot) / 2, 'left', '#8a8a86');
    }

    // ── the side views ─────────────────────────────────────────────────
    //
    // A front view cannot show a standoff: the stud points at the viewer,
    // so 20mm of it is zero pixels. The side view looks along the wall
    // instead - the wall becomes a section and the stud lies across the
    // picture - which is the only place the 20mm that makes C add up to
    // rail + 20 can actually be drawn.
    //
    // The numbers are the solver's own, not retyped here.
    var PH = window.SXPHYS || {};
    var STAND = PH.BRKT_STANDOFF || { flat: 0.024, offset: 0.020 };
    function standMM(which) {
      return Math.round((which === 'offset' ? STAND.offset : STAND.flat) * 1000);
    }
    // Anything cut through: the wall, a rail, the lid skin.
    function sectSlab(x, y, ww, hh) {
      if (ww < 1 || hh < 1) { return; }
      ctx.fillStyle = '#d8d8d4'; ctx.fillRect(x, y, ww, hh);
      ctx.strokeStyle = INK; ctx.lineWidth = 1.3;
      ctx.strokeRect(x + 0.5, y + 0.5, Math.max(1, ww - 1), Math.max(1, hh - 1));
    }
    function railSect(x, y, ww, hh) {
      if (hh < 1) { return; }
      ctx.fillStyle = 'rgba(255,122,0,.3)'; ctx.fillRect(x, y, ww, hh);
      ctx.strokeStyle = ACCENT; ctx.lineWidth = 1.2;
      ctx.strokeRect(x + 0.5, y + 0.5, ww - 1, Math.max(1, hh - 1));
    }
    // A dimension lying across the picture rather than down it.
    function spanDim(y, x1, x2, txt) {
      if (Math.abs(x2 - x1) < 10) { return; }
      ctx.strokeStyle = '#8a8a86'; ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(x2, y); ctx.stroke();
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(x1 + 4, y - 3); ctx.lineTo(x1, y); ctx.lineTo(x1 + 4, y + 3);
      ctx.moveTo(x2 - 4, y - 3); ctx.lineTo(x2, y); ctx.lineTo(x2 - 4, y + 3);
      ctx.stroke();
      tag(txt, (x1 + x2) / 2, y - 8, 'center', '#8a8a86');
    }
    function vDim(x, y1, y2, txt) {
      if (Math.abs(y2 - y1) < 10) { return; }
      ctx.strokeStyle = '#8a8a86'; ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath(); ctx.moveTo(x, y1); ctx.lineTo(x, y2); ctx.stroke();
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(x - 3, y1 + 4); ctx.lineTo(x, y1); ctx.lineTo(x + 3, y1 + 4);
      ctx.moveTo(x - 3, y2 - 4); ctx.lineTo(x, y2); ctx.lineTo(x + 3, y2 - 4);
      ctx.stroke();
      tag(txt, x + 5, (y1 + y2) / 2, 'left', '#8a8a86');
    }
    // One bracket, placed by the face it bolts to.
    //
    //   kind    'flat' or 'offset'
    //   x, y    the point on the mounting face the datum sits at
    //   fx      +1 or -1, which way the part runs across the picture
    //   wall    true when it is bolted to a VERTICAL face instead of a
    //           horizontal one, which costs a quarter turn
    //   standPx how far the ball must stand off that face, in pixels,
    //           from the solver - NOT from the artwork
    //
    // The part is drawn at true scale and only the stud is stretched to
    // reach standPx. Scaling the whole bracket to reach it is what gave
    // the old drawing its long thin legs.
    //
    // Returns where the ball actually landed, in canvas pixels, so the
    // dimension lines hang off the drawing instead of off a second guess
    // at the same geometry.
    function brktArt(kind, x, y, fx, wall, standPx) {
      var art = BRKT_ART[kind === 'offset' ? 'offset' : 'flat'];
      var pp = brktPath(art.d);
      if (!pp) { return null; }
      var s = k2, R = 5;                          // 10mm ball, Design's
      var bx = art.ball[0];
      // The solver's standoff measures perpendicular to the mounting
      // face, which is -y in part space for both parts. Whether the part
      // can actually MEET that number depends on which way its stud runs.
      //
      //   studAx 'y'  the stud is the standoff, so it simply gets drawn
      //               to length. A longer stud is a real variant.
      //   studAx 'x'  the stud is horizontal and the height comes from
      //               the TRIANGLE. Redrawing a stud at a different
      //               height just lays a second bar over the one already
      //               in the path, and leaves the triangle hanging below
      //               the ball. The part's own geometry wins, and
      //               tools/bracket-artwork.test.js fails loudly if the
      //               solver and the artwork ever disagree about it.
      var by = (art.studAx === 'y')
        ? -Math.max(R + 1, standPx / s)
        : art.ball[1];
      ctx.save();
      ctx.translate(x, y);
      if (wall) {
        // Bolted flat to a vertical wall, stud horizontal into the box.
        //
        // A quarter turn ONE way for both walls, then a flip for the
        // right-hand one. Turning the other way instead looks equivalent
        // and is not: it points the apex up the wall on one side and
        // down on the other, so the pair stops being the same part
        // fitted symmetrically. Canvas composes R then S, and
        // R(90).S(s, fx*s) is [[0,-fx*s],[s,0]] - apex down the wall on
        // both sides, stud into the box on both sides.
        ctx.rotate(Math.PI / 2);
        ctx.scale(s, fx * s);
      } else {
        // Hanging under a horizontal face: the part's -y is canvas +y.
        ctx.scale(fx * s, -s);
      }
      ctx.fillStyle = INK;
      ctx.fill(pp, 'evenodd');
      // The stud, drawn to the solver's standoff rather than the part's,
      // and bridged back to the part so the ball is never floating.
      if (art.studAx === 'y') {
        ctx.fillStyle = '#2b2b31';
        ctx.fillRect(bx - 3, by, 6, -3 - by);     // up off the plate
      }                                           // 'x': already in the path
      ctx.fillStyle = ACCENT;
      ctx.strokeStyle = INK;
      ctx.lineWidth = 1.2 / s;
      ctx.beginPath();
      ctx.arc(bx, by, R, 0, 7);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
      // Same transform, applied by hand. translate then scale(fx*s,-s)
      // sends (bx,by) to (x+fx*s*bx, y-s*by); the rotated case sends it
      // to (x-fx*s*by, y+fx*s*bx).
      return wall
        ? { x: x - fx * s * by, y: y + s * bx }
        : { x: x + fx * s * bx, y: y - s * by };
    }


    // ── one box, in section, with both brackets ────────────────────────
    //
    // Looking along the box, so each wall is cut through and the ball
    // studs lie across the picture instead of pointing at you. That is
    // the only view in which a standoff is a visible length, which is why
    // the two square-on panels that used to be here are gone: they could
    // not show the 20mm or the 24mm at all, and with nothing to tell them
    // apart they read as the same picture twice.
    var fTop = dTop + 20, fH = Math.max(60, dH - 58);
    var fBot = fTop + fH;

    // Scale. Everything that has to fit vertically: the lid and its rail,
    // the gap, the box rail, the bracket under it, and some wall below.
    var needMM = Math.max(150, lidRail + boxRail + standMM('offset') + 70);
    // The cap was 1.6 when this had a third of the canvas. With the
    // overview gone it has all of it, and 1.6 left the drawing in the top
    // half with dead space under it.
    var k2 = Math.min(fH / needMM, 2.3);
    var mm2 = function (v) { return Math.max(0, v * k2); };

    // Narrow, with the middle broken out below. At 420 the two walls sat
    // at opposite edges of the canvas with nothing but empty floor
    // between them, and the brackets - the entire point - were 20px tall.
    var boxW2 = Math.min(w - 230, 330);
    var bxL = (w - boxW2) / 2, bxR = bxL + boxW2;
    var wTh = 13;
    // Declared up here because the LID frame is positioned against it: a lid
    // closes into the box, so its frame drops inside the opening the box
    // rail bounds, and the two can never occupy the same depth.
    var brTh = 26;
    var hasBoxRail = boxRail > 0 && mm2(boxRail) > 3;
    var lidInset = wTh + (hasBoxRail ? brTh : 0);

    tag('FRONT VIEW', bxL, dTop - 6);
    tag('looking along the box, both walls cut through',
        w - 14, dTop - 6, 'right', '#a8a8a4');

    // ── the lid, drawn just off its seal ───────────────────────────────
    // Hard down I would be claiming whether the lid rail nests inside the
    // box rail or sits outside it, which depends on the box. Off the seal
    // the drawing only says what is known.
    // A pair of short strokes across the member, the ordinary way of
    // saying "this carries on, it is just not drawn".
    function breakMark(cx, cy) {
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 16;
      ctx.beginPath(); ctx.moveTo(cx - 7, cy); ctx.lineTo(cx + 7, cy); ctx.stroke();
      ctx.strokeStyle = INK; ctx.lineWidth = 1.2;
      [-5, 3].forEach(function (d) {
        ctx.beginPath();
        ctx.moveTo(cx + d - 3, cy + 7); ctx.lineTo(cx + d + 4, cy - 7);
        ctx.stroke();
      });
    }

    var lidTh = 9, lidY = fTop;
    sectSlab(bxL - 6, lidY, boxW2 + 12, lidTh);
    breakMark(bxL + boxW2 / 2, lidY + lidTh / 2);
    tag('LID', bxL - 6, lidY - 7, 'left', '#8a8a86');

    var lrD = mm2(lidRail), lrTh = 13;
    // Hanging off the lid OUTSIDE each wall, clear of everything inside.
    var skD = mm2(lidSkirt);
    if (skD > 2) {
      sectSlab(bxL - 6, lidY + lidTh, 6, skD);
      sectSlab(bxR, lidY + lidTh, 6, skD);
      tag(Math.round(lidSkirt) + 'mm skirt', bxL - 10, lidY + lidTh + skD / 2,
          'right', '#a8a8a4');
    }
    var lidRailY = lidY + lidTh;
    if (lidRail > 0 && lrD > 3) {
      railSect(bxL + lidInset, lidRailY, lrTh, lrD);
      railSect(bxR - lidInset - lrTh, lidRailY, lrTh, lrD);
    }

    // ── the box, below the gap ─────────────────────────────────────────
    var gap = 14;
    var wallY = lidRailY + Math.max(lrD, 4) + gap;
    // Stop the walls a little under the lowest bracket. Running them to
    // the bottom of the panel added height that carried no information
    // and shrank everything that did.
    var wallBot2 = Math.min(fBot, wallY + mm2(boxRail + standMM('offset') + 45));
    sectSlab(bxL, wallY, wTh, wallBot2 - wallY);
    sectSlab(bxR - wTh, wallY, wTh, wallBot2 - wallY);
    tag('BOX', bxR + 6, wallY + 34, 'left', '#8a8a86');
    // The floor, broken in the middle like the lid, because the box is
    // wider than the part of it worth drawing.
    ctx.strokeStyle = '#c9c9c4'; ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(bxL, wallBot2); ctx.lineTo(bxL + boxW2 * 0.4, wallBot2);
    ctx.moveTo(bxR - boxW2 * 0.4, wallBot2); ctx.lineTo(bxR, wallBot2);
    ctx.stroke(); ctx.setLineDash([]);
    breakMark(bxL + boxW2 / 2, wallBot2);
    // The walls stop below the lowest bracket, so the box is cropped here
    // too. The overview used to carry this note; it went with it, and a
    // silently cropped box is the thing that note exists to prevent.
    // Only when the walls really do stop short. boxH > 0 was too loose and
    // put the note on a 150mm box that is drawn in full.
    var shownMM = k2 > 0 ? (wallBot2 - wallY) / k2 : 0;
    if (boxH > shownMM + 5) {
      tag(Math.round(boxH) + 'mm ' + (isWin ? 'frame' : 'box') +
          ', lower part not shown', bxR + 6, wallBot2 - 10, 'left', '#a8a8a4');
    }
    // No words under the break. The two strokes already say it, and with
    // no rail bracket B drops to about this height, so the label landed
    // on the callout and on the flat-bracket warning.

    var brD = mm2(boxRail);
    if (boxRail > 0 && brD > 3) {
      railSect(bxL + wTh, wallY, brTh, brD);
      railSect(bxR - wTh - brTh, wallY, brTh, brD);
    }

    // ── one inboard line, settled before either bracket is drawn ───────
    //
    // Dave, 14/10/2026: "move them inline with the bottom stud". The strut
    // is a straight rod between the two balls and this view looks straight
    // along it, so a stud further inboard than its partner draws a strut
    // skewed across the box.
    //
    // Deciding it from B and then clamping A was not enough. The flat part
    // puts its ball 24 units out from its anchor against the offset's 12,
    // so on a flat lid bracket A's anchor landed inside the wall, the
    // clamp shoved it back out, and the stud went with it - 36 of 648
    // swept combinations came out 12-13px apart, every one of them a flat
    // lid bracket. The LINE has to move instead, which means knowing what
    // both ends need before either of them is drawn.
    function ballDX(kind, wall, standPx) {
      var art = BRKT_ART[kind === 'offset' ? 'offset' : 'flat'];
      var by = (art.studAx === 'y') ? -Math.max(6, standPx / k2) : art.ball[1];
      // Mirrors brktArt's own return: a horizontal face sends the ball out
      // by k2*ball[0], a quarter turn sends it out by -k2*by.
      return wall ? -k2 * by : k2 * art.ball[0];
    }
    var hasRail = boxRail > 0 && brD > 3;
    var bWall = !hasRail;
    var bStand = bWall ? Math.max(15, mm2(standMM(brkB)))
                       : Math.max(13, mm2(standMM(brkB)));
    var aStand = Math.max(15, mm2(standMM(brkA)));
    var bDX = ballDX(brkB, bWall, bStand);
    var aDX = ballDX(brkA, false, aStand);
    // B's mounting face is fixed by the structure - the wall itself when
    // there is no rail, the rail's underside when there is - so B's stud
    // position is not negotiable and A simply goes to it.
    //
    // No clamp. Holding A's anchor out of the wall was tried and is wrong:
    // the flat part reaches 24 units from its anchor to its ball, so on a
    // flat lid bracket the anchor legitimately sits behind the wall line
    // and only the plate's tail overlaps it, in section, in the one
    // configuration the page already refuses. Clamping it instead broke
    // the alignment in 36 cases, and clamping the LINE broke another 72.
    var studX = (bWall ? bxL + wTh : bxL + wTh + mm2(20)) + bDX;
    var bIns = (studX - bDX) - bxL;
    var aIns = (studX - aDX) - bxL;

    // ── bracket B ──────────────────────────────────────────────────────
    var bY;
    if (hasRail && brkB === 'offset') {
      // Bolted up under the rail, then its own step down. The plate's free
      // end sits against the wall and the triangle stands inboard of it,
      // so the stud ends up pointing into the box. That is the whole
      // reason this is the part that goes here.
      var drop = Math.max(13, mm2(standMM('offset')));
      bY = wallY + brD + drop;
      var bBall = null;
      [[bxL + bIns, 1], [bxR - bIns, -1]].forEach(function (p) {
        var r = brktArt('offset', p[0], wallY + brD, p[1], false, drop);
        if (p[1] > 0) { bBall = r; }
      });
      if (bBall) { bY = bBall.y; }
      vDim(bxL + bIns + 46, wallY + brD, bY, standMM('offset') + 'mm');
    } else if (hasRail) {
      // A flat bracket cannot go under a rail - its stud faces the floor.
      //
      // Drawn as the real part in the orientation it would actually land
      // in, rather than as a bare bar. This used to be a 3mm fillRect and
      // a pair of arrows, which is what Dave was looking at when he asked
      // why the bottom stud "shows like that" - there was no bracket and
      // no ball down there at all, just a stick.
      var fBall = null;
      [[bxL + bIns, 1], [bxR - bIns, -1]].forEach(function (p) {
        var r = brktArt(brkB, p[0], wallY + brD, p[1], false, bStand);
        if (p[1] > 0) { fBall = r; }
        // The arrows say which way the stud points, and start at the ball
        // rather than in mid-air.
        if (r) {
          ctx.strokeStyle = '#c0392b'; ctx.lineWidth = 2.2;
          ctx.beginPath();
          ctx.moveTo(r.x, r.y + 10); ctx.lineTo(r.x, r.y + 26);
          ctx.moveTo(r.x - 5, r.y + 20);
          ctx.lineTo(r.x, r.y + 27);
          ctx.lineTo(r.x + 5, r.y + 20);
          ctx.stroke();
        }
      });
      bY = fBall ? fBall.y : wallY + brD + 22;
      // Clamped above the caption line, which lives at h - 16.
      tag('a flat bracket faces the floor here - use the offset',
          w / 2, Math.min(bY + 42, h - 34), 'center', '#c0392b');
    } else {
      // No rail, so it bolts flat to the wall and stands off inboard. A
      // vertical face, so the part takes a quarter turn: its length runs
      // down the wall and the stud points across into the box.
      bY = wallY + Math.max(22, Math.min(fBot - wallY - 26, mm2(cM)));
      var wBall = null;
      [[bxL + wTh, 1], [bxR - wTh, -1]].forEach(function (p) {
        var r = brktArt(brkB, p[0], bY - mm2(15.5), p[1], true, bStand);
        if (p[1] > 0) { wBall = r; }
      });
      spanDim(bY + 20, bxL + wTh, wBall ? wBall.x : bxL + wTh + bStand,
              standMM(brkB) + 'mm');
    }
    // Clear of the drop dimension, which runs out to about +73.
    numTag(1, bxL + wTh + 86, bY + 24);

    // ── bracket A, on the same line ────────────────────────────────────
    //
    // Not on the frame's inner face. Dave settled it: the lid brackets bolt
    // to the underside, which is why the solver now puts the ball at frame
    // depth PLUS the standoff rather than minus it. This used to draw a
    // vertical plate on a face the bracket does not touch, so the figure
    // and the maths disagreed about where the hole goes.
    var aY = null, n2x = 0, n2y = 0;
    if (lidRail > 0 && lrD > 3) {
      // Plate flat on the bottom of the frame, ball hanging below it.
      var aUnder = lidRailY + lrD;
      aY = aUnder + 6 + aStand;
      var aBall = null;
      [[bxL + aIns, 1], [bxR - aIns, -1]].forEach(function (p) {
        var r = brktArt(brkA, p[0], aUnder, p[1], false, aStand);
        if (p[1] > 0) { aBall = r; }
      });
      if (aBall) { aY = aBall.y; }
      vDim(bxL + aIns + 28, aUnder, aY, standMM(brkA) + 'mm');
      n2x = bxL + aIns + 62; n2y = aUnder + 10;
    } else {
      // No lid rail: flat on the underside, stud straight down.
      aY = lidY + lidTh;
      var aTop = aY, aBall2 = null;
      [[bxL + aIns, 1], [bxR - aIns, -1]].forEach(function (p) {
        var r = brktArt(brkA, p[0], aTop, p[1], false, aStand);
        if (p[1] > 0) { aBall2 = r; }
      });
      if (aBall2) { aY = aBall2.y; }
      vDim(bxL + aIns + 26, aTop, aY, standMM(brkA) + 'mm');
      // With no rail the standoff dimension hangs BELOW the bracket, so
      // the callout cannot go there as well - it goes outboard instead.
      n2x = bxL + 14; n2y = aY + 18;
    }
    // Beside the bracket it names, clear of whichever side the standoff
    // dimension ended up on.
    numTag(2, n2x, n2y);

    // ── the rail depths, called out down the left ──────────────────────
    if (lidRail > 0 && lrD > 3) {
      vDim(bxL - 20, lidRailY, lidRailY + lrD, Math.round(lidRail) + 'mm');
    }
    if (boxRail > 0 && brD > 3) {
      vDim(bxR + 22, wallY, wallY + brD, Math.round(boxRail) + 'mm');
    }
    // Left margin, not centred. Centred it sat under the 2 callout, whose
    // 16px disc the collision test was not measuring - it only knew about
    // the single glyph drawn on top.
    tag('lid shown just off the seal', 14, wallY - gap / 2, 'left', '#a8a8a4');

    capFit('BOTH BRACKETS, LOOKING ALONG THE BOX', w / 2, h - 16,
           w - 28, '600', 10, '#1c1e22');
    capFit((boxRail > 0 ? 'B bolts up under the box rail, ball '
              + standMM('offset') + 'mm clear of the plate'
              : 'B bolts flat to the wall, ball standing '
              + standMM(brkB) + 'mm in')
           + ' \u00b7 ' + ('A bolts ' + (brkA === 'offset' ? 'up' : 'flat')
              + ' under the lid' + (lidRail > 0 ? ' frame' : '')
              + ', ball ' + standMM(brkA) + 'mm down'),
           w / 2, h - 4, w - 28, '400', 9, '#8a8a86');

  }

  function draw3d(cv, S) {
    var f = fit(cv), ctx = f.ctx, w = f.w, h = f.h;
    var yaw = S.yaw || 0, pitch = 0.62;
    var th = (S.ang || 0) * Math.PI / 180;
    var W = 1.5, H = 0.62, D = 1.0;
    var g = geom(S);
    var bx = W / 2, bz = D / 2;
    var pl = g.kind === 'lid' ? D : H * 1.45;
    var LW = g.kind === 'lid'
      ? function (x, d) { return [x, H + d * Math.sin(th), -bz + d * Math.cos(th)]; }
      : g.kind === 'window'
        ? function (x, d) { return [x, H - d * Math.cos(th), bz + d * Math.sin(th)]; }
        : function (x, d) { return [x, d * Math.cos(th), bz + d * Math.sin(th)]; };
    // Tilt the whole model, so a sloped tray looks sloped here too.
    var tR = (S.tilt || 0) * Math.PI / 180, ct = Math.cos(tR), stt = Math.sin(tR);
    var raw = function (x, y, z) {
      var y2 = y * ct - z * stt, z2 = y * stt + z * ct;
      var xr = x * Math.cos(yaw) + z2 * Math.sin(yaw);
      var zr = -x * Math.sin(yaw) + z2 * Math.cos(yaw);
      return [xr, -y2 * Math.cos(pitch) + zr * Math.sin(pitch)];
    };
    // The camera does NOT move with the lid. The panel turns about the x
    // axis, so its plane always contains x, and a fixed isometric is bound to
    // look straight down it at some opening angle - around 40 degrees here,
    // where the panel goes briefly edge-on. Swinging the camera through that
    // fixed it and made the whole box turn with it, which reads as the
    // brackets moving. The brackets are bolted on; the rotate arrows are
    // there for anyone who wants to look round the flat spot.
    var pts = [];
    [-bx, bx].forEach(function (x) {
      [0, H].forEach(function (y) { [-bz, bz].forEach(function (z) { pts.push(raw(x, y, z)); }); });
    });
    [-bx, bx].forEach(function (x) {
      [0, pl].forEach(function (d) { var q = LW(x, d); pts.push(raw(q[0], q[1], q[2])); });
    });
    var xs = pts.map(function (q) { return q[0]; }), ys = pts.map(function (q) { return q[1]; });
    var mnX = Math.min.apply(null, xs), mxX = Math.max.apply(null, xs);
    var mnY = Math.min.apply(null, ys), mxY = Math.max.apply(null, ys);
    var mL = 20, mR = 20, mT = 20, mB = 44;
    var SC = Math.min((w - mL - mR) / (mxX - mnX), (h - mT - mB) / (mxY - mnY));
    var cx = mL + (w - mL - mR) / 2 - SC * (mnX + mxX) / 2;
    var cy = mT + (h - mT - mB) / 2 - SC * (mnY + mxY) / 2;
    var P = function (x, y, z) { var r = raw(x, y, z); return [cx + r[0] * SC, cy + r[1] * SC]; };
    var poly = function (ps, fill, stroke, lw) {
      ctx.beginPath();
      ps.forEach(function (p, i) { i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]); });
      ctx.closePath();
      if (fill) { ctx.fillStyle = fill; ctx.fill(); }
      if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = lw || 1.5; ctx.stroke(); }
    };
    ctx.strokeStyle = '#efefed'; ctx.lineWidth = 1;
    for (var i = -3; i <= 3; i++) {
      var a1 = P(i * 0.5, 0, -1.6), b1 = P(i * 0.5, 0, 1.6),c1 = P(-1.6, 0, i * 0.5), d1 = P(1.6, 0, i * 0.5);
      ctx.beginPath(); ctx.moveTo(a1[0], a1[1]); ctx.lineTo(b1[0], b1[1]); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(c1[0], c1[1]); ctx.lineTo(d1[0], d1[1]); ctx.stroke();
    }
    poly([P(-bx, 0, -bz), P(bx, 0, -bz), P(bx, 0, bz), P(-bx, 0, bz)], 'rgba(23,23,26,.04)', '#c4c4c0', 1);
    poly([P(-bx, 0, -bz), P(bx, 0, -bz), P(bx, H, -bz), P(-bx, H, -bz)], 'rgba(23,23,26,.03)', '#c4c4c0', 1);
    poly([P(-bx, 0, -bz), P(-bx, 0, bz), P(-bx, H, bz), P(-bx, H, -bz)], 'rgba(23,23,26,.05)', INK, 1.2);
    poly([P(bx, 0, -bz), P(bx, 0, bz), P(bx, H, bz), P(bx, H, -bz)], 'rgba(23,23,26,.05)', INK, 1.2);
    poly([P(-bx, 0, bz), P(bx, 0, bz), P(bx, H, bz), P(-bx, H, bz)], 'rgba(23,23,26,.06)', INK, 1.5);

    // The frame rail, round the top of the box. Fig.01 and the fitting sheet
    // both draw it; without it here the 3D was the one figure that could not
    // say why bracket B sits where it does. Bracket B lands under this band.
    var railF = (S.boxH > 0 && S.railMM > 0) ? Math.min(S.railMM / S.boxH, 0.85) : 0;
    if (railF > 0.012) {
      // At the real ratio a 90mm rail on a 600mm box came out six pixels tall
      // here, because the open lid takes most of the frame and leaves the box
      // small. Geometrically right and completely invisible, which is no use
      // to anyone. Hold it to a floor of about five pixels on screen - this
      // is a schematic, and the millimetres are printed beside it.
      var boxPx = Math.abs(P(0, 0, bz)[1] - P(0, H, bz)[1]);
      if (boxPx > 1 && boxPx * railF < 5) { railF = Math.min(0.85, 5 / boxPx); }
      var ry = H * (1 - railF);
      [[[-bx, ry, -bz], [bx, ry, -bz], [bx, H, -bz], [-bx, H, -bz]],
       [[-bx, ry, -bz], [-bx, ry, bz], [-bx, H, bz], [-bx, H, -bz]],
       [[bx, ry, -bz], [bx, ry, bz], [bx, H, bz], [bx, H, -bz]],
       [[-bx, ry, bz], [bx, ry, bz], [bx, H, bz], [-bx, H, bz]]
      ].forEach(function (q) {
        poly(q.map(function (v) { return P(v[0], v[1], v[2]); }),
             'rgba(255,122,0,.42)', ACCENT, 1.3);
      });
    }
    var LP = function (x, d) { var q = LW(x, d); return P(q[0], q[1], q[2]); };
    // Lid fill says whether the strut is carrying it yet, so the 3D tells the
    // same story as Fig.01 instead of just spinning.
    var held = S.kick != null && (S.ang || 0) >= S.kick;
    poly([LP(-bx, 0), LP(bx, 0), LP(bx, pl), LP(-bx, pl)],
         held ? 'rgba(255,122,0,.22)' : 'rgba(23,23,26,.07)', INK, 2);
    // The panel's own frame, as a band round the underside of the lid. The
    // inward normal of the lid plane, which for a lid is (0,-cos,+sin).
    var lrF = (S.panelLen > 0 && S.lidRailMM > 0)
      ? Math.min(S.lidRailMM / S.panelLen, 0.35) : 0;
    if (lrF > 0.005 && g.kind === 'lid') {
      var dRail = pl * lrF;
      var nY = -Math.cos(th), nZ = Math.sin(th);
      var off = function (q) { return [q[0], q[1] + nY * dRail, q[2] + nZ * dRail]; };
      var edges = [[LW(-bx, 0), LW(bx, 0)], [LW(-bx, pl), LW(bx, pl)],
                   [LW(-bx, 0), LW(-bx, pl)], [LW(bx, 0), LW(bx, pl)]];
      edges.forEach(function (e) {
        var q = [e[0], e[1], off(e[1]), off(e[0])]
          .map(function (v) { return P(v[0], v[1], v[2]); });
        poly(q, 'rgba(255,122,0,.3)', ACCENT, 1.1);
      });
    }
    var hgY = g.kind === 'hatch' ? 0 : H, hgZ = g.kind === 'lid' ? -bz : bz;
    var hA = P(-bx, hgY, hgZ), hB = P(bx, hgY, hgZ);
    ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(hA[0], hA[1]); ctx.lineTo(hB[0], hB[1]); ctx.stroke();
    [hA, hB].forEach(function (p) {
      ctx.fillStyle = '#fff'; ctx.strokeStyle = INK; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(p[0], p[1], 4.5, 0, 7); ctx.fill(); ctx.stroke();
    });
    // The same two holes Fig.01 draws, from the same solved millimetres. This
    // used to be 65% along the panel and 64% up the wall, which is why the
    // two figures disagreed with each other and with the A/B/C table.
    var fA = (S.panelLen > 0 && S.aMM != null) ? S.aMM / S.panelLen : 0.65;
    var fB = (S.panelLen > 0 && S.bMM != null) ? S.bMM / S.panelLen : 0.30;
    var fC = (S.boxH > 0 && S.cMM != null) ? S.cMM / S.boxH : 0.36;
    fA = Math.max(0.08, Math.min(fA, 0.96));
    fB = Math.max(0.03, Math.min(fB, 0.80));
    fC = Math.max(0.04, Math.min(fC, 0.92));
    // Both ends on the same offset from the side. They used to sit at
    // bx-0.10 and bx-0.14, which ran each strut diagonally across the box; a
    // strut works in one plane, parallel to the side it is mounted off.
    var pairs = S.ns === 2 ? [[-(bx - 0.12), -(bx - 0.12)], [bx - 0.12, bx - 0.12]]
                           : [[0, 0]];
    pairs.forEach(function (pr) {
      var wz = g.kind === 'lid' ? -bz + D * fB : bz - D * fB;
      var a = P(pr[0], g.kind === 'hatch' ? H * 0.62 : H * (1 - fC), wz);
      var b = LP(pr[1], pl * fA);
      ctx.strokeStyle = '#55555a'; ctx.lineWidth = 3; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(a[0], a[1]);
      ctx.lineTo(a[0] + (b[0] - a[0]) * 0.48, a[1] + (b[1] - a[1]) * 0.48); ctx.stroke();
      ctx.strokeStyle = ACCENT; ctx.lineWidth = 7;
      ctx.beginPath(); ctx.moveTo(a[0] + (b[0] - a[0]) * 0.44, a[1] + (b[1] - a[1]) * 0.44);
      ctx.lineTo(b[0], b[1]); ctx.stroke();
      [a, b].forEach(function (p) {
        ctx.fillStyle = '#fff'; ctx.strokeStyle = INK; ctx.lineWidth = 1.6;
        ctx.beginPath(); ctx.arc(p[0], p[1], 3.8, 0, 7); ctx.fill(); ctx.stroke();
      });
      if (pr === pairs[pairs.length - 1]) {
        ctx.fillStyle = INK; ctx.font = '500 10px "IBM Plex Mono", monospace';
        ctx.fillText('A', b[0] + 8, b[1] - 6);
        ctx.fillText('B', a[0] + 8, a[1] + 13);
      }
    });
    ctx.fillStyle = '#6e6e6b'; ctx.font = '400 9px "IBM Plex Mono", monospace';
    ctx.fillText('HINGE', hB[0] + 9, hB[1] - 7);
    ctx.fillText(S.ns === 2 ? 'TWO STRUTS' : 'ONE STRUT', 12, h - 14);
    if (S.kick != null) {
      ctx.fillStyle = held ? '#ff7a00' : '#6e6e6b';
      ctx.fillText(held ? 'STRUT HOLDS IT \u00b7 PUSH TO SHUT' : 'LIFT IT BY HAND', 12, h - 28);
    }
  }

  root.SXFIG = { draw2d: draw2d, draw3d: draw3d, drawDrill: drawDrill,
                 drawMounts: drawMounts,
                 geom: geom, fit: fit };
})(window);
